import { Client } from '@colyseus/sdk'
import type { Room } from '@colyseus/sdk'
import { MSG_QUEUE_FAILED, MSG_SEAT, MSG_SEAT_OPTIONS, PARTY_ROOM, PartyState } from '@runkr/schema'
import type { PartyJoinOptions, RaceJoinOptions, SeatMsg, SeatReservation } from '@runkr/schema'
import type { PartySnapshot } from './partyView'

/** One joined party room, as the `Party` controller sees it (a fake in tests). */
export interface PartyLink {
  readonly sessionId: string
  /** Every state change, and the current state at once. */
  onState(cb: (snap: PartySnapshot) => void): void
  onSeat(cb: (seat: SeatReservation) => void): void
  onQueueFailed(cb: () => void): void
  /** The room is gone (left, closed, or the reconnection gave up). */
  onLeave(cb: () => void): void
  send(type: 'queue' | 'cancel'): void
  /** `"seatOptions"`: our race join options (no token) for our next seats. */
  sendOptions(options: RaceJoinOptions): void
  leave(): Promise<void>
}

export interface PartyConnector {
  create(): Promise<PartyLink>
  join(roomId: string, code: string): Promise<PartyLink>
}

export function partySnapshot(s: PartyState): PartySnapshot {
  return {
    code: s.code,
    leaderSessionId: s.leaderSessionId,
    status: s.status,
    members: [...s.members].map((m) => ({
      sessionId: m.sessionId,
      displayName: m.displayName,
      connected: m.connected,
    })),
  }
}

function wrap(room: Room<unknown, PartyState>): PartyLink {
  return {
    sessionId: room.sessionId,
    onState: (cb) => {
      room.onStateChange((s) => cb(partySnapshot(s)))
      // The first state may have arrived with the join, before this listener.
      if (room.state.code) cb(partySnapshot(room.state))
    },
    onSeat: (cb) => room.onMessage(MSG_SEAT, (m: SeatMsg) => cb(m.reservation)),
    onQueueFailed: (cb) => room.onMessage(MSG_QUEUE_FAILED, () => cb()),
    onLeave: (cb) => room.onLeave(() => cb()),
    send: (type) => room.send(type),
    sendOptions: (options) => room.send(MSG_SEAT_OPTIONS, options),
    leave: () => room.leave(true).then(() => undefined),
  }
}

/**
 * The party room over `@colyseus/sdk` (T2.7). `options()` gives our race join options at each
 * call (`GameClient.joinOptions`: fresh token, dev name, runner, ride, …); the party room keeps
 * them, minus the token, for our race seats.
 */
export function sdkPartyConnector(
  serverUrl: string,
  options: () => Promise<PartyJoinOptions>,
): PartyConnector {
  const client = new Client(serverUrl)
  return {
    create: async () =>
      wrap(await client.create<PartyState>(PARTY_ROOM, await options(), PartyState)),
    join: async (roomId, code) =>
      wrap(await client.joinById<PartyState>(roomId, { ...(await options()), code }, PartyState)),
  }
}
